import { useEffect, useRef, useCallback } from "react";

/**
 * Syncs a boolean open state with browser history so that
 * pressing the back button closes the dialog/drawer/panel
 * instead of navigating away.
 */
export function useBackButton(
  open: boolean,
  onClose: () => void,
  key: string = "dialog"
) {
  const pushedRef = useRef(false);

  const handlePopState = useCallback(
    (e: PopStateEvent) => {
      if (pushedRef.current) {
        pushedRef.current = false;
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      // Push a dummy state so back button pops it
      window.history.pushState({ overlay: key }, "");
      pushedRef.current = true;
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // If closing normally (not via back button), remove the dummy state
      if (pushedRef.current) {
        pushedRef.current = false;
        window.history.back();
      }
    };
  }, [open, key, handlePopState]);
}
